import { types } from 'mobx-state-tree'

import { CHANGE_ICON } from '../ActivityFeedIcon'
import { ReferenceFreightieOrUser } from '../Actors'
import { TAvatarType } from '../avatars/AvatarType'
import { getAvatar, getAvatarType, getTitleFromActor } from '../avatars/avatarUtils'
import { IActivityMessageSection, IActivityTitle, TEXT, UPDATE_TABLE } from '../messages/MessageSectionType'
import { Activity } from './Activity'

const fieldsNameMap = {
  containerNumbers: {
    label: 'activity-feed_shipment_update_container_numbers',
    singularLabel: 'activity-feed_shipment_update_container_number',
  },
  containerCustomerReferences: {
    label: 'activity-feed_shipment_update_container_customer_references',
    singularLabel: 'activity-feed_shipment_update_container_customer_reference',
  },
  sealNumbers: { label: 'Seal numbers', singularLabel: 'Seal number' },
  customerReference: { label: 'Customer reference' },
  additionalInformation: { label: 'activity-feed_shipment_update_additional_info' },
  incoTerms: { label: 'activity-feed_shipment_update_incoterm' },
  hblNumber: { label: 'activity-feed_shipment_update_house_bl' },
  hblType: {
    label: 'activity-feed_shipment_update_hbl_type_selection',
    translations: {
      original: 'shipment_details-shipping_instructions-hbl-type-original',
      express: 'shipment_details-shipping_instructions-hbl-type-express',
    },
  },
  pickupDetails: { label: 'activity-feed_shipment_update_pickup_address' },
  deliveryDetails: { label: 'activity-feed_shipment_update_delivery_address' },
  consigneeContact: { label: 'activity-feed_shipment_update_consignee_details' },
  shipperContact: { label: 'activity-feed_shipment_update_shipper_details' },
}

export const ShipmentUpdateActivity = types
  .compose(
    'ShipmentUpdateActivity',
    Activity,
    types.model({
      actor: ReferenceFreightieOrUser,
      changedFields: types.map(types.string),
    })
  )
  .preProcessSnapshot((snapshot) => {
    const { freightieAuthor_id, userAuthor_id } = snapshot
    return {
      ...snapshot,
      actor: freightieAuthor_id || userAuthor_id,
    }
  })
  .views((self) => ({
    get isSingleLineField(): boolean {
      const singleLineTextKeys = ['shipperContact', 'consigneeContact']
      const changedFieldsEntries = self.changedFields.entries()
      if (changedFieldsEntries.length === 1) {
        const key = `${changedFieldsEntries[0][0]}`
        return singleLineTextKeys.indexOf(key) !== -1
      }
      return false
    },
  }))
  .views((self) => ({
    get boldText(): IActivityMessageSection {
      return {
        type: TEXT,
        getStyle: (theme) => ({ display: 'block', margin: theme.spacing(0.5, 0, 1.2) }),
        variant: 'body2-medium',
        content: 'activity-feed_shipment_info_updated',
        translate: true,
      }
    },
    getPluralorSingularText(changedField) {
      const fieldName = fieldsNameMap[changedField[0]]
      if (changedField[0] === changedField[1]) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix template expression
        return [`${fieldName.label}`, 'activity-feed_shipment_info_value_updated']
      }
      if (changedField[1].split(',').length <= 1 && fieldName.singularLabel) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix template expression
        return [`${fieldName.singularLabel}`, changedField[1]]
      }
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix template expression
      return [`${fieldName.label}`, fieldName.translations ? fieldName.translations[changedField[1]] : changedField[1]]
    },
    get tableText(): IActivityMessageSection {
      const changedFieldsEntries = self.changedFields.entries()
      if (self.isSingleLineField) {
        return {
          type: TEXT,
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: Fix template expression
          content: `${fieldsNameMap[changedFieldsEntries[0][1]].label}_updated`,
          translate: true,
        }
      }
      const tableContent = changedFieldsEntries.map((keyValuePair) => this.getPluralorSingularText(keyValuePair))

      return {
        type: UPDATE_TABLE,
        tableContent,
        style: { marginRight: '7px' },
        translate: true,
      }
    },
  }))
  .views((self) => ({
    get activityIcon(): string {
      return CHANGE_ICON
    },
    get avatarType(): TAvatarType {
      return getAvatarType(self.actor)
    },
    get avatar(): string {
      return getAvatar(self.actor)
    },
    get messageContent(): IActivityMessageSection[] {
      return [self.boldText, self.tableText]
    },
    get title(): IActivityTitle {
      return getTitleFromActor(self.actor)
    },
  }))

export type TShipmentUpdateActivity = typeof ShipmentUpdateActivity.Type
